import { Component, OnDestroy, OnInit } from '@angular/core';
import { LocalLoadService } from '../shared/local-load.service';
import { ImportFileLoadsResult, ImportFileLoadsResultReason } from '../shared/file-import/import-file-loads-result';
import { ImportLoadsStatus } from '../shared/import-loads-status';
import { LoaderService } from '../../shared/services/loader.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { interval, Observable, Subscription } from 'rxjs';
import { CarrierService } from '../../user-dashboard/shared/carrier.service';
import { TmsType } from '../../user-dashboard/shared/tms-type.model';
import { ActivatedRoute } from '@angular/router';
import { ImportTmsLoadsResult, ImportTmsLoadsResultReason } from '../shared/tms-import/import-tms-loads-result';
import { LocalLoadImportService } from '../shared/local-load-import.service';
import { Moment } from 'moment';
import { LocalImport } from '../shared/local-import.model';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-import-local-loads',
  templateUrl: './import-local-loads.component.html',
  styleUrls: ['./import-local-loads.component.scss'],
})

export class ImportLocalLoadsComponent implements OnInit, OnDestroy {

  files: File[];
  lastImportDate: Moment;
  tmsResult: ImportTmsLoadsResult;
  fileResult: ImportFileLoadsResult;

  done  = false;
  doing = false;
  succeed: boolean;
  partiallySucceed: boolean;
  tmsSyncIsInProgress: boolean;

  globalErrorMessage: string;
  currentTmsSyncStatus: string;
  tmsSyncProgressPercent: string;

  showCarrierSelection     = false;
  showBlujaySource         = false;
  showBlueyonderSource     = false;
  showImportArea           = true;
  invalidFileType          = true;
  carriers: Carrier[]      = [];
  selectedCarrier: Carrier;
  TmsType                  = TmsType;
  selectedSource: TmsType;
  selectedSourceIsTms = false;
  carrierIdFromQueryParams = null;

  tmsSyncStatusCheckerSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private carrierService: CarrierService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private localLoadService: LocalLoadService,
    private localLoadImportService: LocalLoadImportService
  ) {
    route.queryParams.subscribe(queryParams => {
      this.carrierIdFromQueryParams = queryParams.carrierId;
    });
  }

  ngOnInit() {
    this.reset();

    if (this.authService.canManageCarriers()) {
      this.showCarrierSelection = true;
      this.showImportArea       = false;

      this.loader.startLoad(false);
      this.carrierService.getAll()
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(carriers => {
          this.carriers = carriers;
          if (this.carrierIdFromQueryParams) {
            this.selectedCarrier = carriers.find((carrier) => carrier.id === this.carrierIdFromQueryParams);
            this.changeCarrier();
          }
        });
    } else {
      const userProfile         = this.authService.getUserProfile();
      this.showBlujaySource     = userProfile.carrier.tmsType === TmsType.BLUJAY && userProfile.featureProfile.tmsLoadsSynchronization;
      this.showBlueyonderSource = userProfile.carrier.tmsType === TmsType.BLUEYONDER && userProfile.featureProfile.tmsLoadsSynchronization;
    }

    this.tmsSyncStatusCheckerSubscription = interval(30000)
      .subscribe(() => {
        if (this.tmsSyncIsInProgress) {
          this.loadCarrierLastImport();
        }
      });
  }

  changeFile() {
    const fileNameParts  = this.files[0].name.split('.');
    const fileExtension  = fileNameParts[fileNameParts.length - 1];
    this.invalidFileType = fileExtension !== 'csv';
    if (this.invalidFileType) {
      this.globalErrorMessage = this.translate.instant('local-loads.import.file.failed.invalid-type',
        { fileExtension: fileExtension.toUpperCase() });
    }
  }

  isFileImportInvalid() {
    return !this.files || this.files.length !== 1 || this.invalidFileType;
  }

  changeCarrier() {
    this.showBlujaySource = this.selectedCarrier
      && this.selectedCarrier.tmsType === TmsType.BLUJAY
      && this.selectedCarrier.featureProfile.tmsLoadsSynchronization;
    this.showBlueyonderSource = this.selectedCarrier
      && this.selectedCarrier.tmsType === TmsType.BLUEYONDER
      && this.selectedCarrier.featureProfile.tmsLoadsSynchronization;
    this.showImportArea   = !!this.selectedCarrier;
    this.reset();
    this.selectedSourceIsTms = false;

    if (this.selectedSource === TmsType.BLUJAY && !this.showBlujaySource) {
      this.selectedSource = null;
    }

    if (this.selectedSource === TmsType.BLUEYONDER && !this.showBlueyonderSource) {
      this.selectedSource = null;
    }

    if (this.selectedSource === TmsType.BLUJAY || this.selectedSource === TmsType.BLUEYONDER) {
      this.loadCarrierLastImport();
      this.selectedSourceIsTms = true;
    }
  }

  changeSource() {
    this.reset();
    this.selectedSourceIsTms = false;

    if (this.selectedSource === TmsType.BLUJAY || this.selectedSource === TmsType.BLUEYONDER) {
      this.loadCarrierLastImport();
      this.selectedSourceIsTms = true;
    }
  }

  loadCarrierLastImport() {
    let lastImportObservable: Observable<LocalImport> = this.localLoadImportService.getLastImportForSelfCarrier();

    if (this.authService.canManageSelfLocalLoads()) {
      lastImportObservable = this.localLoadImportService.getLastImportForSelfCarrier();
    } else if (this.authService.canManageAllLocalLoads()) {
      lastImportObservable = this.localLoadImportService.getLastImportForCarrier(this.selectedCarrier.id);
    }

    this.doing = true;
    this.done = false;
    this.loader.startLoad();

    lastImportObservable
      .pipe(
        finalize(() => {
          this.doing = false;
          this.done = true;
          this.loader.stopLoad();
        })
      ).subscribe(result => {
        if (result) {
          this.setTmsResult(result.report, result.status);
        }
      });
  }

  importLoads() {
    let importObservable: Observable<ImportFileLoadsResult> = this.localLoadService.importLoadsForSelfCarrier(this.files[0]);

    if (this.authService.canManageSelfLocalLoads()) {
      importObservable = this.localLoadService.importLoadsForSelfCarrier(this.files[0]);
    } else if (this.authService.canManageAllLocalLoads()) {
      importObservable = this.localLoadService.importLoadsForCarrier(this.files[0], this.selectedCarrier);
    }

    this.loader.startLoad();
    this.doing = true;
    this.done  = false;

    importObservable
      .pipe(finalize(() => {
        this.done  = true;
        this.doing = false;
        this.loader.stopLoad();
      })).subscribe(result => {
        this.files = [];

        this.fileResult = result;

        this.succeed = result.status === ImportLoadsStatus.SUCCEED;

        if (!this.succeed) {
          switch (result.reason) {
            case ImportFileLoadsResultReason.EMPTY_FILE:
              this.globalErrorMessage = this.translate.instant('local-loads.import.file.failed.empty-file');
              break;
            case ImportFileLoadsResultReason.EMPTY_ROWS:
              this.globalErrorMessage = this.translate.instant('local-loads.import.file.failed.empty-rows');
              break;
            case ImportFileLoadsResultReason.COULD_NOT_EXTRACT_HEADER_COLUMNS:
              this.globalErrorMessage = this.translate.instant('local-loads.import.file.failed.could-not-extract-header-columns');
              break;
            case ImportFileLoadsResultReason.INVALID_HEADER_COLUMNS:
              if (result.missingColumns.length > 0) {
                this.globalErrorMessage = this.translate.instant(
                  'local-loads.import.file.failed.missing-columns',
                  { columns: result.missingColumns.map(c => `'${c.name}'`).join(', ') }
                );
              } else if (result.unexpectedColumns.length > 0) {
                this.globalErrorMessage = this.translate.instant(
                  'local-loads.import.file.failed.unexpected-columns',
                  { columns: result.unexpectedColumns.map(c => `'${c}'`).join(', ') }
                );
              } else {
                this.globalErrorMessage = this.translate.instant('local-loads.import.file.failed.invalid-header-columns');
              }

              break;
          }
        }
        this.showImportArea = false;
      }, (error: HttpErrorResponse) => {
        console.error(error);
        this.succeed        = false;
        this.showImportArea = false;
        this.toastr.error(error.error.message || this.translate.instant('local-loads.import.file.errors.default'));
      });
  }

  reset() {
    this.files               = [];
    this.succeed             = true;
    this.partiallySucceed    = false;
    this.tmsSyncIsInProgress = false;
    this.done                = false;
    this.doing               = false;
    this.fileResult          = null;
    this.globalErrorMessage  = null;
    this.showImportArea      = true;
    this.tmsResult           = null;
  }

  synchronizeTmsLoads() {
    this.reset();
    this.currentTmsSyncStatus   = this.translate.instant('local-loads.import.tms.status.on-going');
    this.tmsSyncProgressPercent = '0%'
    this.tmsSyncIsInProgress    = true;

    let synchronizeObservable: Observable<ImportTmsLoadsResult> = this.localLoadService.synchronizeTmsLoadsForSelfCarrier();

    if (this.authService.canManageSelfLocalLoads()) {
      synchronizeObservable = this.localLoadService.synchronizeTmsLoadsForSelfCarrier();
    } else if (this.authService.canManageAllLocalLoads()) {
      synchronizeObservable = this.localLoadService.synchronizeTmsLoadsForCarrier(this.selectedCarrier);
    }

    this.loader.startLoad();
    this.done  = false;
    this.doing = true;

    synchronizeObservable
      .pipe(finalize(() => this.loader.stopLoad())
      ).subscribe(result => {
        this.doing = false;
        this.done  = true;
        this.setTmsResult(null, ImportLoadsStatus.ON_GOING);
      }, (error: HttpErrorResponse) => {
        console.error(error);
        this.doing = false;
        this.done  = true;
        this.succeed          = false;
        this.partiallySucceed = false;
        this.toastr.error(error.error.message || this.translate.instant('local-loads.import.tms.errors.default'));
      });
  }

  setTmsResult(result: ImportTmsLoadsResult, status: ImportLoadsStatus) {
    this.tmsResult           = result;
    this.lastImportDate      = result ? result.importedAt : null;
    this.succeed             = status === ImportLoadsStatus.SUCCEED || status === ImportLoadsStatus.PARTIALLY_SUCCEED;
    this.partiallySucceed    = status === ImportLoadsStatus.PARTIALLY_SUCCEED;
    this.tmsSyncIsInProgress = !(status && (status === ImportLoadsStatus.SUCCEED || status === ImportLoadsStatus.PARTIALLY_SUCCEED
      || status === ImportLoadsStatus.FAILED));

    if (this.tmsSyncIsInProgress) {
      this.showCurrentStatusOfTmsSync(status);
    }

    if (!this.succeed) {
      if (result && result.reason === ImportTmsLoadsResultReason.BLUJAY_CREDENTIALS_ERROR) {
        this.globalErrorMessage = this.translate.instant('local-loads.import.tms.failed.blujay-credentials-error');
      }
    }
  }

  showCurrentStatusOfTmsSync(status: ImportLoadsStatus) {
    if (status === ImportLoadsStatus.ON_GOING) {
      this.currentTmsSyncStatus   = this.translate.instant('local-loads.import.tms.status.on-going');
      this.tmsSyncProgressPercent = '0%';
    } else if (status === ImportLoadsStatus.FETCHING_LOADS_FROM_TMS) {
      this.currentTmsSyncStatus   = this.translate.instant('local-loads.import.tms.status.fetching-loads-from-tms');
      this.tmsSyncProgressPercent = '1%';
    } else if (status === ImportLoadsStatus.UPDATING_LOCAL_LOADS) {
      this.currentTmsSyncStatus   = this.translate.instant('local-loads.import.tms.status.updating-local-loads');
      this.tmsSyncProgressPercent = '40%';
    } else if (status === ImportLoadsStatus.REMOVING_NOT_FETCHED_FUTURE_LOADS) {
      this.currentTmsSyncStatus   = this.translate.instant('local-loads.import.tms.status.removing-not-fetched-future-loads');
      this.tmsSyncProgressPercent = '80%';
    } else {
      this.currentTmsSyncStatus   = this.translate.instant('local-loads.import.tms.status.completed');
      this.tmsSyncProgressPercent = '100%';
    }
  }

  downloadCsvTemplate() {
    this.loader.startLoad();

    this.localLoadImportService.downloadCsvImportTemplate()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(
        () => {
        },
        (error: HttpErrorResponse) => {
          console.error(error);
          this.toastr.error(error.error.message || this.translate.instant('local-loads.import.file.errors.download-template'));
        });
  }

  ngOnDestroy() {
    if (this.tmsSyncStatusCheckerSubscription) {
      this.tmsSyncStatusCheckerSubscription.unsubscribe();
    }
  }
}
